import classes from './ScheduleList.module.scss';
import moment from "moment";
import ScheduleListItem from "./ScheduleListItem/ScheduleListItem";
import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {Collapse, Space} from "antd";
import {
    compareOneDayLessons,
    DEFAULT_DATE_FORMAT,
    getMonthNameByNumber,
    getNextLessonToEnd,
    getUniqueMonths,
    getWeekFilterSelectionInfo,
    ifGrayOutScheduleItem,
    lessonMightBeHighlightedForDate
} from "../../../services/scheduleService";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import useDefaultWeek from "../ScheduleFilters/useDefaultWeek";
import {getDateOfWeekdayOptions} from "../CommonSchedule/common";
import {faChevronRight} from '@fortawesome/free-solid-svg-icons';

const { Panel } = Collapse;
const { Fragment } = React;

export const Mode = {
    regularLessons: 1,
    todayAndTomorrow: 2,
    exams: 3
};

const ScheduleList = ({ mode = Mode.regularLessons, isCurrentPeriod, lessonsData, startOfTermDate,
                          scheduleType, style }) => {

    const todayAndTomorrowLabels = ['Сегодня', 'Завтра'];
    const weekDayLabels = ['', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

    const [lessons, setLessons] = useState([]);

    const currentWeek = useSelector(state => state.schedule.currentWeekNumber);
    const defaultWeek = useDefaultWeek(startOfTermDate);
    const weekFilters = useSelector(state => state.schedule.weekFilters);

    const weekFilterSelection = getWeekFilterSelectionInfo(weekFilters, currentWeek, defaultWeek);
    const dateOfWeekdayOptions = getDateOfWeekdayOptions(weekFilterSelection, startOfTermDate, isCurrentPeriod);
    const dateOfWeekdayOption = dateOfWeekdayOptions.find(o => o.shouldApply);

    useEffect(() => {
        setLessons(Object.entries(lessonsData ?? {}));
    }, [lessonsData]);

    const isCurrentWeekOrCurrentDay = (lesson) => {
        if (lesson.weekNumber) {
            return lesson.weekNumber.includes(currentWeek);
        }
        if (lesson.dateLesson) {
            const date = moment(lesson.dateLesson, DEFAULT_DATE_FORMAT);
            return date.isSame(moment(), 'day');
        }
        return false;
    }

    const getLessonsByWeekDay = (weekDay, isLastDate) => {
        let dayLessonsArray = null;
        if (!dateOfWeekdayOption || dateOfWeekdayOption.shouldDisplayDayLessons(weekDay)) {
            dayLessonsArray = lessons.find(d => d[0] === weekDayLabels[weekDay]);
        }
        const dayLessons = (dayLessonsArray && dayLessonsArray.length === 2) ? dayLessonsArray[1] : null;
        if (!dayLessons || dayLessons.length === 0) {
            return (
                <div className={classes.emptyDayContainer}
                     style={isLastDate ? {borderRadius: '0 0 8px 8px'} : null}>
                    Нет занятий
                </div>);
        }

        const currentDay = moment();
        const nextLessonToEnd = getNextLessonToEnd(dayLessons, currentDay.valueOf());

        return dayLessons.sort(compareOneDayLessons).map((item, index) => {
            const isLastLesson = index === dayLessons.length - 1;
            const shouldGrayOut = ifGrayOutScheduleItem(item, weekFilterSelection.onlyDefaultWeekSelected, isCurrentPeriod);

            const lessonPeriodIsCurrent = item.endLessonTime === nextLessonToEnd?.endLessonTime;
            const shouldHighlightLesson = dayLessonsArray[0] === weekDayLabels[currentDay.isoWeekday()]
                && lessonMightBeHighlightedForDate(item)
                && isCurrentWeekOrCurrentDay(item)
                && lessonPeriodIsCurrent;

            return (<Fragment key={JSON.stringify(item)}>
                <ScheduleListItem lesson={item}
                                  scheduleType={scheduleType}
                                  style={shouldGrayOut ? {opacity: '0.5'} : {}}
                                  isCurrentLesson={shouldHighlightLesson}/>
                {(!isLastDate || !isLastLesson) && <div className={classes.horizontalDivider}/>}
            </Fragment>);
        });
    }

    const getExamsByDate = (date, isLastDate) => {
        const examsByDate = lessonsData
            .filter(exam => exam.dateLesson === date)
            .sort(compareOneDayLessons);
        return examsByDate.map((el, index) => {
            const isLastLesson = index === examsByDate.length - 1;
            return (<Fragment key={JSON.stringify(el)}>
                <ScheduleListItem lesson={el}
                                  scheduleType={scheduleType}
                                  displayDate={false}/>
                {(!isLastDate || !isLastLesson) && <div className={classes.horizontalDivider}/>}
            </Fragment>);
        });
    }

    const isMonthInThePast = (monthAndYearArray) => {
        const today = moment();
        if (monthAndYearArray[1] === today.year()) {
            return monthAndYearArray[0] - 1 < today.month();
        }
        return monthAndYearArray[1] < today.year();
    }

    const getExams = () => {
        const getExpandIcon = (panelProps) => {
            return <FontAwesomeIcon icon={faChevronRight}
                                    className={`${classes.expandIcon} 
                                    ${panelProps.isActive ? classes.rotated : null}`}/>;
        }
        const sessionMonths = getUniqueMonths(lessonsData);

        const monthSections = [];
        for (const monthAndYear of sessionMonths) {
            const monthAndYearArray = monthAndYear.split('.').map(Number);

            const examByDayElements = [];
            const examsByMonth = lessonsData
                .filter(exam => moment(exam.dateLesson, DEFAULT_DATE_FORMAT).month() + 1 === monthAndYearArray[0]);

            const uniqueDates = new Set(examsByMonth.map(e => e.dateLesson));
            const uniqueDatesSorted = Array.from(uniqueDates)
                .sort((a, b) => {
                    return moment(a, DEFAULT_DATE_FORMAT).diff((moment(b, DEFAULT_DATE_FORMAT)), 'days');
                });
            uniqueDatesSorted.forEach((date, index, array) => {
                const dateOfExam = moment(date, DEFAULT_DATE_FORMAT);

                examByDayElements.push(
                    <div key={`exams_${date}`}>
                        <div className={classes.scheduleDayHeader}>
                            {`${dateOfExam.format(DEFAULT_DATE_FORMAT)} - ${weekDayLabels[dateOfExam.isoWeekday()]}`}
                        </div>
                        {getExamsByDate(date, index === array.length - 1)}
                    </div>);
            });

            const key = `monthSection_${monthAndYear}`;
            monthSections.push(<Collapse defaultActiveKey={!isMonthInThePast(monthAndYearArray) ? key : undefined}
                                         expandIcon={getExpandIcon}>
                <Panel header={<span className={classes.examsMonthName}>
                    {getMonthNameByNumber(monthAndYearArray[0])}
                </span>}
                       key={key}>
                    {examByDayElements}
                </Panel>
            </Collapse>);
        }

        return (<Space size={15} direction="vertical" className={classes.monthCollapse}>
            {monthSections}
        </Space>);
    }

    const getWeekDayHeader = (weekDay) => {
        const today = moment().startOf('day');

        let weekDayLabel = weekDayLabels[weekDay];
        let todayTomorrow = null;
        let date = null;

        let isToday = false;
        const currentDay = dateOfWeekdayOption?.getDate(weekDay);
        if (currentDay) {
            if (today.isSame(currentDay, 'day')) {
                todayTomorrow = ' (сегодня)';
                isToday = true;
            } else if (currentDay.diff(today, 'days') === 1) {
                todayTomorrow = ' (завтра)';
            }
            date = currentDay.format(DEFAULT_DATE_FORMAT);
        }
        return (<div className={`${classes.scheduleDayHeader} ${isToday ? classes.today : null}`}>
            <span>
                {weekDayLabel}
                <span className={classes.extraInfo}>{todayTomorrow}</span>
            </span>
            <span className={classes.extraInfo}>{date}</span>
        </div>);
    }

    const getTodayTomorrowLabel = (index) => {
        const currentDay = moment().add(index, 'days');

        return (<Fragment>
            <span>{todayAndTomorrowLabels[index]}</span>
            <span className={classes.extraInfo}>{currentDay.format(DEFAULT_DATE_FORMAT)}</span>
        </Fragment>);
    }

    const getLessonsForTodayAndTomorrow = () => {
        const today = moment();
        const scheduleElements = [];

        let currentDay;
        for (let i = 0; i < todayAndTomorrowLabels.length; i++) {
            currentDay = moment(today).add(i, 'days');

            scheduleElements.push(<div key={`todayTomorrow_${i}`}>
                <div className={classes.scheduleDayHeader}>
                    {getTodayTomorrowLabel(i)}
                </div>
                {getLessonsByWeekDay(currentDay.isoWeekday(), i === 1)}
            </div>);
        }
        return <div className={classes.scheduleListContainer}>
            {scheduleElements}
        </div>;
    }

    const getLessonsForWeek = () => {
        const scheduleElements = [];
        if (!weekFilters || weekFilters.every(w => !w)) return null;

        for (let i = 1; i < weekDayLabels.length; i++) {
            scheduleElements.push(<div key={weekDayLabels[i]}>
                {getWeekDayHeader(i)}
                {getLessonsByWeekDay(i, i === weekDayLabels.length - 1)}
            </div>);
        }
        return <div className={classes.scheduleListContainer}>
            {scheduleElements}
        </div>;
    }

    const getScheduleByMode = () => {
        switch (mode) {
            case Mode.regularLessons:
                return getLessonsForWeek();
            case Mode.todayAndTomorrow:
                return getLessonsForTodayAndTomorrow();
            case Mode.exams:
                return getExams();
            default:
                return null;
        }
    }

    const getContainerClassByMode = () => {
        switch (mode) {
            case Mode.regularLessons:
                return classes.regularLessons;
            case Mode.todayAndTomorrow:
                return classes.todayAndTomorrowLessons;
            case Mode.exams:
                return classes.exams;
            default:
                return null;
        }
    }

    return (<div className={getContainerClassByMode()} style={style}>
        {getScheduleByMode()}
    </div>);
}

export default ScheduleList;
